import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  alpha,
  Grid,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import KeyOutlinedIconMui from "@mui/icons-material/KeyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import "./copytrade.css";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const CopyTradeDashboard = () => {
  const navigate = useNavigate();
  const { brokerBasicInfo, btncolor } = useSelector((state) => state);
  const [openIndex, setOpenIndex] = useState(null);
  const [manageExpanded, setManageExpanded] = useState(false);

  const [traderRole, setTraderRole] = useState(
    localStorage.getItem("traderRole") || "trader"
  );
  const [traderStatus, setTraderStatus] = useState(false);
  const [copyTraderStatus, setCopyTraderStatus] = useState(false);

  const handleSelectRole = (role) => {
    setTraderRole(role);
    setTraderStatus(role === "trader");
    setCopyTraderStatus(role === "copyTrader");
    localStorage.setItem("traderRole", role);
  };

  const handleClick = (item, index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
    if (item?.link) navigate(item.link);
  };

  useEffect(() => {
    localStorage.setItem("traderRole", traderRole);
  }, []);

  const menuItems = [
    { label: "Add API Key", icon: KeyOutlinedIcon, link: "/add-apiKey", desc: "Connect your exchange API" },
    { label: "My Bots", icon: SmartToyIcon, link: "/my-bots", desc: "Manage your trading bots" },
    { label: "Marketplace", icon: AccountBalanceOutlinedIcon, link: "/marketplace", desc: "Browse top traders & bots" },
    {
      label: traderRole === "copyTrader" ? "Whom I Follow" : "My Followers",
      icon: PeopleAltOutlinedIcon,
      link: traderRole === "copyTrader" ? "/whom-I-follow" : "/my-followers",
      desc: traderRole === "copyTrader" ? "View traders you follow" : "See your followers",
    },
    {
      label: "Copy-Trading History",
      icon: AutoStoriesOutlinedIcon,
      link: "/copy-trading-history",
      desc: "View your trade history",
    },
  ];

  const manageSubItems = [
    { label: "Payment Received", icon: PaymentsOutlinedIcon, link: "/my-payouts" },
    { label: "API Keys and Bots", icon: KeyOutlinedIconMui, link: "/manage-copytrading" },
  ];

  const features = [
    `${localStorage.getItem("exchange") || "Exchange"} Copy Trading is an automated Crypto trading platform that allows you to start trading and investing in cryptocurrency seamlessly.`,
    "Copy trade the best crypto traders with transparent track record, trade yourself using the advanced trading terminal or create fully automated trading bot.",
  ];

  return (
    <Box sx={pageWrapper}>
      {/* Hero Banner */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <SmartToyOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>Copy Trading</Typography>
            <Typography sx={heroSubtitle}>
              Automated crypto trading — follow top traders or run your own bots
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Role Selector */}
      <Box sx={roleSelectorWrap}>
        <Typography sx={roleSelectorLabel}>Choose Your Role</Typography>
        <Box sx={roleToggleWrap}>
          {[
            { value: "trader", label: "Trader", desc: "Trade & earn from followers" },
            { value: "copyTrader", label: "Copy Trader", desc: "Follow top traders' strategies" },
          ].map((role) => {
            const isActive = traderRole === role.value;
            return (
              <Box
                key={role.value}
                onClick={() => handleSelectRole(role.value)}
                sx={{
                  ...roleCard,
                  borderColor: isActive ? btncolor : "rgba(125,125,125,0.18)",
                  backgroundColor: isActive
                    ? alpha(btncolor || "#1976d2", 0.08)
                    : "var(--body-color-less)",
                  boxShadow: isActive ? `0 0 0 2px ${alpha(btncolor || "#1976d2", 0.35)}` : "none",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      ...roleIconWrap,
                      backgroundColor: isActive
                        ? alpha(btncolor || "#1976d2", 0.12)
                        : "rgba(125,125,125,0.08)",
                      color: isActive ? btncolor : "var(--text-color)",
                    }}
                  >
                    <ManageAccountsOutlinedIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ ...roleCardTitle, color: isActive ? btncolor : "var(--text-color)" }}>
                      {role.label}
                    </Typography>
                    <Typography sx={roleCardDesc}>{role.desc}</Typography>
                  </Box>
                </Box>
                {isActive && (
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: btncolor, ml: "auto", flexShrink: 0 }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Navigation Menu + Info */}
      <Box sx={contentRow}>
        {/* Left: Navigation Cards */}
        <Box sx={navSection}>
          <Typography sx={sectionHeading}>Navigation</Typography>
          <Box sx={navGrid}>
            {menuItems
              .filter((_, i) => !(traderRole === "copyTrader" && i === 1))
              .map((item, i) => (
                <Box
                  key={item.label}
                  onClick={() => navigate(item.link)}
                  sx={navCard}
                >
                  <Box
                    sx={{
                      ...navCardIcon,
                      backgroundColor: alpha(btncolor || "#1976d2", 0.1),
                      color: btncolor,
                    }}
                  >
                    <item.icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={navCardLabel}>{item.label}</Typography>
                    <Typography sx={navCardDesc}>{item.desc}</Typography>
                  </Box>
                  <ArrowForwardIosIcon sx={{ fontSize: 12, opacity: 0.35, flexShrink: 0 }} />
                </Box>
              ))}

            {/* Manage Copy-Trading (only for Trader role) */}
            {traderRole !== "copyTrader" && (
              <Box>
                <Box
                  onClick={() => setManageExpanded((prev) => !prev)}
                  sx={navCard}
                >
                  <Box
                    sx={{
                      ...navCardIcon,
                      backgroundColor: alpha(btncolor || "#1976d2", 0.1),
                      color: btncolor,
                    }}
                  >
                    <ConstructionOutlinedIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={navCardLabel}>Manage Copy-Trading</Typography>
                    <Typography sx={navCardDesc}>Payouts and API key management</Typography>
                  </Box>
                  <ArrowForwardIosIcon
                    sx={{
                      fontSize: 12,
                      opacity: 0.35,
                      flexShrink: 0,
                      transform: manageExpanded ? "rotate(90deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </Box>
                {manageExpanded && (
                  <Box sx={{ pl: 2, pt: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {manageSubItems.map((sub) => (
                      <Box
                        key={sub.label}
                        onClick={() => navigate(sub.link)}
                        sx={subNavCard}
                      >
                        <sub.icon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                        <Typography sx={subNavLabel}>{sub.label}</Typography>
                        <ArrowForwardIosIcon sx={{ fontSize: 10, opacity: 0.3, ml: "auto" }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Right: Info */}
        <Box sx={infoSection}>
          <Box sx={infoCard}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <TrendingUpOutlinedIcon sx={{ color: btncolor, fontSize: 20 }} />
              <Typography sx={infoCardTitle}>About Copy Trading</Typography>
            </Box>
            {features.map((text, i) => (
              <Box key={i} sx={featureRow}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: btncolor, flexShrink: 0, mt: "2px" }} />
                <Typography sx={featureText}>{text}</Typography>
              </Box>
            ))}
            <Box
              component="a"
              href={`${window.location.origin}/help/copy-trade.html`}
              target="_blank"
              rel="noopener noreferrer"
              sx={learnMoreLink}
            >
              <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
                How Copy Trading works
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

// Role selector
const roleSelectorWrap = {
  mb: 3,
};

const roleSelectorLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--text-color)",
  opacity: 0.5,
  mb: 1.25,
};

const roleToggleWrap = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 1.5,
};

const roleCard = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: 2,
  borderRadius: "14px",
  border: "1.5px solid",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": { transform: "translateY(-1px)" },
};

const roleIconWrap = {
  width: 40,
  height: 40,
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const roleCardTitle = {
  fontSize: "0.95rem",
  fontWeight: 700,
  lineHeight: 1.2,
};

const roleCardDesc = {
  fontSize: "0.75rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

// Content row
const contentRow = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 380px" },
  gap: 2.5,
  alignItems: "start",
};

const navSection = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  p: { xs: 2, md: 2.5 },
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const sectionHeading = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--text-color)",
  opacity: 0.45,
  mb: 1.5,
};

const navGrid = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
};

const navCard = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: 1.5,
  borderRadius: "10px",
  cursor: "pointer",
  border: "1px solid rgba(125,125,125,0.1)",
  backgroundColor: "var(--body-color)",
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: "var(--body-color-less2)",
    borderColor: "rgba(125,125,125,0.22)",
  },
};

const navCardIcon = {
  width: 36,
  height: 36,
  borderRadius: "9px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const navCardLabel = {
  fontSize: "0.88rem",
  fontWeight: 600,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const navCardDesc = {
  fontSize: "0.72rem",
  color: "var(--text-color)",
  opacity: 0.5,
  mt: 0.15,
};

const subNavCard = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  py: 1,
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: "var(--body-color-less2)",
  transition: "all 0.15s ease",
  "&:hover": { opacity: 0.8 },
};

const subNavLabel = {
  fontSize: "0.82rem",
  fontWeight: 500,
  color: "var(--text-color)",
};

// Info section
const infoSection = {};

const infoCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  p: { xs: 2, md: 2.5 },
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const infoCardTitle = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const featureRow = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  mb: 1.5,
};

const featureText = {
  fontSize: "0.83rem",
  color: "var(--text-color)",
  opacity: 0.7,
  lineHeight: 1.65,
};

const learnMoreLink = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  mt: 1.5,
  color: "var(--text-color)",
  opacity: 0.6,
  textDecoration: "none",
  "&:hover": { opacity: 1 },
  transition: "opacity 0.15s",
};

export default CopyTradeDashboard;
